import {useCallback, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {Languages, RefreshCw, WifiOff} from "lucide-react";
import {useCapabilities} from "../../../shared/auth/CapabilitiesContext";
import {DataState} from "../../../shared/components/DataState";
import {PageToolbar, ToolbarInput, ToolbarSelect} from "../../../shared/components/PageToolbar";
import {appTextsKeys, loadAppTextSections, loadAppTextsScope} from "../appTextsApi";
import {
    OFFLINE_COPY,
    OFFLINE_TITLE,
    appTextsErrorMessage,
    defaultCountryId,
    defaultLocale,
    localeLabel,
    readStoredLocale,
    writeStoredLocale,
} from "../appTextsContracts";
import {AppTextsEditor} from "./AppTextsEditor";
import {AppTextsSectionList} from "./AppTextsSectionList";
import {ConfirmDialog} from "./ConfirmDialog";
import {useTranslation, i18n} from "../../../shared/i18n";


const SCOPE_STALE_TIME = 5 * 60_000;
const EMPTY = [];

const Loading = () => (
    <div aria-label={i18n.t("sections.content.p160")} className="content-media-loading content-texts-loading">
        <span /><span /><span />
    </div>
);

export const AppTextsWorkspace = ({headerCountryScope, onDirtyChange}) => {
    const {t} = useTranslation();
    const capabilities = useCapabilities();
    const scopeQuery = useQuery({
        queryKey: appTextsKeys.scope(),
        queryFn: loadAppTextsScope,
        staleTime: SCOPE_STALE_TIME,
    });
    const scope = scopeQuery.data;
    const countries = Array.isArray(scope?.countries) ? scope.countries : EMPTY;
    const languages = Array.isArray(scope?.languages) ? scope.languages : EMPTY;

    const [countryChoice, setCountryChoice] = useState(null);
    const [localeChoice, setLocaleChoice] = useState(readStoredLocale);
    const [sectionChoice, setSectionChoice] = useState(null);
    const [search, setSearch] = useState("");
    const [dirty, setDirty] = useState(false);
    const [pendingSwitch, setPendingSwitch] = useState(null);

    // The tab keeps its own country choice: switching the header scope would remount the page.
    const chosenCountry = countries.find((country) => String(country.id) === String(countryChoice ?? ""));
    const countryId = chosenCountry ? chosenCountry.id : defaultCountryId(scope, headerCountryScope);
    const country = countries.find((item) => String(item.id) === String(countryId ?? ""));
    const locale = defaultLocale(scope, localeChoice);
    const language = languages.find((item) => item.code === locale);
    const canManage = scope?.can_edit === true && capabilities.hasAction("content.manage");

    const sectionsQuery = useQuery({
        queryKey: appTextsKeys.sections(countryId, locale),
        queryFn: () => loadAppTextSections({countryId, locale}),
        enabled: countryId !== null && countryId !== undefined && Boolean(locale),
    });
    const sections = Array.isArray(sectionsQuery.data?.sections) ? sectionsQuery.data.sections : EMPTY;
    const sectionName = sections.some((item) => item.name === sectionChoice) ? sectionChoice : sections[0]?.name ?? null;

    const handleDirtyChange = useCallback((value) => {
        setDirty(value);
        onDirtyChange?.(value);
    }, [onDirtyChange]);

    const guard = (action) => {
        if (dirty) setPendingSwitch(() => action);
        else action();
    };
    const cancelSwitch = useCallback(() => setPendingSwitch(null), []);
    const confirmSwitch = () => {
        const action = pendingSwitch;
        setPendingSwitch(null);
        action?.();
    };

    const selectCountry = (value) => guard(() => setCountryChoice(value));
    const selectLocale = (value) => guard(() => {
        setLocaleChoice(value);
        writeStoredLocale(value);
    });
    const selectSection = (name) => {
        if (name === sectionName) return;
        guard(() => setSectionChoice(name));
    };

    const head = (
        <div className="content-workspace-head">
            <div>
                <h2>{t("sections.content.p28")}</h2>
                <p>{t("sections.content.p161")}</p>
            </div>
        </div>
    );

    // isPending, not isLoading: a scope request paused by an offline browser is neither
    // loading nor failed, and must not fall through to «Нет стран в вашем доступе».
    if (scopeQuery.isPending) {
        if (scopeQuery.fetchStatus === "paused") {
            return (
                <div className="content-workspace content-texts">
                    {head}
                    <div className="content-readonly-note"><WifiOff size={16} /><span>{OFFLINE_TITLE}. {OFFLINE_COPY}</span></div>
                </div>
            );
        }
        return <div className="content-workspace content-texts">{head}<Loading /></div>;
    }
    if (scopeQuery.isError) {
        const notDeployed = scopeQuery.error?.response?.status === 404;
        return (
            <div className="content-workspace content-texts">
                {head}
                <div className="content-texts-state">
                    <DataState
                        title={notDeployed ? t("sections.content.p162") : t("sections.content.p163")}
                        copy={notDeployed ? t("sections.content.p164") : appTextsErrorMessage(scopeQuery.error)}
                    />
                    {notDeployed ? null : <button className="admin-row-action" onClick={() => scopeQuery.refetch()} type="button"><RefreshCw size={14} /> {t("sections.content.p70")}</button>}
                </div>
            </div>
        );
    }
    if (!countries.length) {
        return <div className="content-workspace content-texts">{head}<DataState title={t("sections.content.p165")} copy={t("sections.content.p166")} /></div>;
    }
    if (!languages.length) {
        return <div className="content-workspace content-texts">{head}<DataState title={t("sections.content.p167")} copy={t("sections.content.p168")} /></div>;
    }

    return (
        <div className="content-workspace content-texts">
            {head}

            <PageToolbar>
                <ToolbarSelect
                    aria-label={t("sections.content.p169")}
                    disabled={countries.length <= 1}
                    label={t("sections.content.p170")}
                    onChange={(event) => selectCountry(event.target.value)}
                    value={String(countryId ?? "")}
                >
                    {countries.map((item) => <option key={item.id} value={String(item.id)}>{item.name}</option>)}
                </ToolbarSelect>
                <ToolbarSelect aria-label={t("sections.content.p171")} label={t("sections.content.p172")} onChange={(event) => selectLocale(event.target.value)} value={locale ?? ""}>
                    {languages.map((item) => <option key={item.code} value={item.code}>{localeLabel(item)}</option>)}
                </ToolbarSelect>
                <ToolbarInput
                    aria-label={t("sections.content.p173")}
                    label={t("sections.content.p174")}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={t("sections.content.p175")}
                    type="search"
                    value={search}
                />
            </PageToolbar>

            <p className="content-texts-scope-copy">
                {t("sections.content.p176")} <strong>{country?.name || "—"}</strong>
                {language ? <> {t("sections.content.p177")} <strong>{localeLabel(language)}</strong></> : null}
            </p>
            {language && Number(language.sections_in_card) === 0 ? (
                <div className="content-readonly-note"><Languages size={16} /><span>{t("sections.content.p178")}</span></div>
            ) : null}

            <div className="content-texts-layout">
                <AppTextsSectionList activeName={sectionName} onSelect={selectSection} query={sectionsQuery} sections={sections} />
                {sectionName ? (
                    <AppTextsEditor
                        canManage={canManage}
                        countryId={countryId}
                        key={`${countryId}:${locale}:${sectionName}`}
                        locale={locale}
                        onDirtyChange={handleDirtyChange}
                        search={search}
                        section={sectionName}
                    />
                ) : null}
            </div>

            <ConfirmDialog
                cancelLabel={t("sections.content.p30")}
                confirmLabel={t("sections.content.p31")}
                copy={t("sections.content.p32")}
                onCancel={cancelSwitch}
                onConfirm={confirmSwitch}
                open={Boolean(pendingSwitch)}
                title={t("sections.content.p33")}
            />
        </div>
    );
};
